import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  Inject,
  Optional,
  Self,
  OnChanges,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import {
  NgrxFormControlDirective,
  FormControlValueTypes,
  NGRX_FORM_VIEW_ADAPTER,
  FormGroupState,
  FormControlState
} from 'ngrx-forms';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import {
  ActionsSubject,
  Store,
  MemoizedSelector
} from '@ngrx/store';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';
import { BASE_GROUP_STATE_SELECTOR } from './tokens';
import {
  filter,
  map,
  tap,
  distinctUntilChanged,
  debounceTime,
  first
} from 'rxjs/operators';
import { deepEqual } from '@digitaix/helpers';

@Directive({
  selector: '[dxFormControl]'
})
export class DxFormControlDirective<
  TStateValue extends FormControlValueTypes,
  TViewValue = TStateValue
> extends NgrxFormControlDirective<TStateValue, TViewValue>
  implements OnInit, OnDestroy, OnChanges {
  @Input() public dxFormControl: string | null = null;

  public readonly baseSelector: MemoizedSelector<any, FormGroupState<any>>;

  private _subscriptions = new Subscription();

  constructor(
    el: ElementRef,
    @Optional() @Inject(DOCUMENT) dom: any,
    actionsSubject: ActionsSubject,
    @Self() @Optional() @Inject(NGRX_FORM_VIEW_ADAPTER) viewAdapters: any,
    @Self() @Optional() @Inject(NG_VALUE_ACCESSOR) valueAccessors: any,
    public readonly store$: Store<any>,
    public readonly logger: NGXLogger,
    @Inject(BASE_GROUP_STATE_SELECTOR) baseSelector: any,
    public readonly view: ViewContainerRef
  ) {
    super(el, dom, actionsSubject, viewAdapters, valueAccessors);
    this.baseSelector = baseSelector;
  }

  public ngOnInit(): void {
    if (!this.dxFormControl) {
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxFormControl
        }] the dxFormControl input is not defined`
      );
    }
    this.logger.trace(
      `[DxFormControlDirective::ngOnInit::${this.dxFormControl}] call`
    );

    if (!this.baseSelector) {
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxFormControl
        }] the BASE_GROUP_STATE_SELECTOR Injector is not defined`
      );
    }

    this._subscriptions.add(
      this.store$.select(this.baseSelector)
          .pipe(
            filter(Boolean),
            first(),
            map(baseState => this.applyControlPath(baseState, this.dxFormControl)),
            tap(state => this.updateNgrxFormControlState(state)),
            tap(() => super.ngOnInit())
          ).subscribe()
    );

    this._subscriptions.add(
      this.store$
        .select(this.baseSelector)
        .pipe(
          filter(Boolean),
          debounceTime(250),
          distinctUntilChanged(deepEqual),
          map(baseState => this.applyControlPath(baseState, this.dxFormControl)),
          distinctUntilChanged(deepEqual),
          tap(state => this.updateNgrxFormControlState(state)),
        )
        .subscribe()
    );
  }

  private updateNgrxFormControlState(state: FormControlState<any>): void {
    this.logger.trace(
      `[DxFormControlDirective::updateNgrxFormControlState::${this.dxFormControl}] call`,
      state
    );
    this.ngrxFormControlState = state;
    (<any>this.view)._data.componentView.component.ngrxFormControl = state;
  }

  private applyControlPath(
    baseState: FormGroupState<any>,
    controlPath: string
  ): FormControlState<any> {
    const targetState = controlPath.split('/').reduce((state, controlId) => {
      if (state.controls.hasOwnProperty(controlId)) {
        return state.controls[controlId];
      }
      this.logger.error(
        `[DxFormControlDirective::ngOnInit::${controlPath}] control path is not valid`,
        { dxFormControl: controlPath, baseState }
      );
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${controlPath}] control path is not valid`
      );
    }, baseState);

    return targetState as FormControlState<any>;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['dxFormControl'] &&
      changes['dxFormControl'].previousValue &&
      changes['dxFormControl'].previousValue !==
        changes['dxFormControl'].currentValue
    ) {
      this.logger.error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxFormControl
        }] the dxFormControl input should not change`,
        changes['dxFormControl']
      );
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxFormControl
        }] the dxFormControl input should not change`
      );
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._subscriptions = new Subscription();
  }
}
